import { DocumentNode } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { addMonths, getUnixTime } from "date-fns/esm";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Datepicker from "ds/components/Datepicker";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";
import useTypedContext from "hooks/useTypedContext";
import { BillingTier } from "types/generated";
import {
  getDateInDays,
  stringIsRequired,
  validateDateIsAfterNow,
  validateDateIsWithinDays,
} from "utils/formValidators";
import { getDocsUrl } from "utils/getDocsUrl";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";

import { PersonalSettingsApiKeysCreateFields } from "./types";
import usePersonalApiKeysCreate from "./useCreate";

const MAX_EXPIRATION_DAYS = 180;

type PersonalSettingsApiKeysCreateDrawerProps = {
  refetchQueries?: DocumentNode[];
};

const PersonalSettingsApiKeysCreateDrawer = createDrawer(
  ({ refetchQueries }: PersonalSettingsApiKeysCreateDrawerProps) => {
    const drawer = useModal();
    const { tier } = useTypedContext(SubscriptionContext);

    const isWarningShown =
      tier === BillingTier.Cloud ||
      tier === BillingTier.V3Cloud ||
      tier === BillingTier.V4Starter ||
      tier === BillingTier.V5Starter;

    const trackSegmentAnalyticsEvent = useAnalytics({
      page: AnalyticsPagePersonal.PersonalApiKeys,
    });

    const createForm = useForm<PersonalSettingsApiKeysCreateFields>({
      defaultValues: {
        name: "",
        description: "",
        expiresAt: addMonths(new Date(), 3),
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = createForm;

    const handleCloseDrawer = () => {
      drawer.resolve();
      drawer.hide();
    };

    const { onCreate, loading } = usePersonalApiKeysCreate({
      refetchQueries,
      callback: handleCloseDrawer,
    });

    const onSubmit: SubmitHandler<PersonalSettingsApiKeysCreateFields> = (formFields) => {
      trackSegmentAnalyticsEvent("Create Confirm");
      onCreate({
        name: formFields.name.trim(),
        description: formFields.description.trim(),
        expiresAt: getUnixTime(formFields.expiresAt),
      });
    };

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...createForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Create API key" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <FormField label="Name" error={errors.name?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter API key name"
                  error={!!errors.name}
                  {...register("name", {
                    validate: stringIsRequired("Name field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <Controller
              name="expiresAt"
              rules={{
                validate: {
                  isAfterNow: validateDateIsAfterNow,
                  isWithinMaxDays: validateDateIsWithinDays(
                    MAX_EXPIRATION_DAYS,
                    `Expiration date can't be more than ${MAX_EXPIRATION_DAYS} days from now`
                  ),
                },
              }}
              render={({ field, fieldState }) => (
                <FormField
                  label="Expiration date"
                  error={fieldState.error?.message}
                  helperText={`Choose a date within the next ${MAX_EXPIRATION_DAYS} days.`}
                >
                  <Datepicker
                    date={field.value}
                    onChange={field.onChange}
                    minDate={new Date()}
                    maxDate={getDateInDays(MAX_EXPIRATION_DAYS)}
                    maxDateTooltip={`Beyond the ${MAX_EXPIRATION_DAYS}-day limit for personal API keys.`}
                    hasError={!!fieldState.error}
                    withTime
                    fullWidth
                  />
                </FormField>
              )}
            />

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter API key description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <Box margin="large 0 0 0">
              <Banner variant="info">
                Upon API key creation, a file will be generated with API key secret token details.
                Please save this file somewhere safe as it will never be available again.
              </Banner>
            </Box>

            {isWarningShown && (
              <Box margin="large 0 0 0">
                <Banner variant="warning">
                  <Typography tag="p" variant="p-body3" margin="0 0 medium 0">
                    API keys are virtual users and are billed like regular users, too. Thus, each
                    API key used (exchanged for a token) during any given billing cycle counts
                    against the total number of users.
                  </Typography>
                  <ReadMoreDocsLink
                    docsUrl={getDocsUrl("/integrations/api#authenticating-with-the-graphql-api")}
                  />
                </Banner>
              </Box>
            )}

            <DrawerFooter>
              <DrawerFooterActions>
                <Button variant="secondary" onPress={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick} loading={loading}>
                  Create
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showPersonalSettingsApiKeysCreateDrawer = createDrawerTrigger(
  PersonalSettingsApiKeysCreateDrawer
);
